// React and related imports
import React, { useId, useState, useEffect } from 'react';

// Headless UI and Lucide imports
import { Popover } from '@headlessui/react';
import { Clock } from 'lucide-react';

// i18n Translation
import { useTranslation } from 'react-i18next';

// Utils
import { formatLocalizedTime } from '../../../utils/formatLocalizedTime';

interface TimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
}

const hours = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, '0')
);
const minutes = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, '0')
);
const periods = ['AM', 'PM'];

export const TimePicker: React.FC<TimePickerProps> = ({
  id,
  value,
  onChange,
  label,
}) => {
  const generatedId = useId();
  const effectiveID = id || generatedId;
  const { t } = useTranslation();

  const [selectedHour, setSelectedHour] = useState('--');
  const [selectedMinute, setSelectedMinute] = useState('--');
  const [selectedPeriod, setSelectedPeriod] = useState('--');

  // Initialize state from passed value
  useEffect(() => {
    if (value) {
      const [time, period] = value.split(' ');
      const [hour, minute] = time.split(':');
      setSelectedHour(hour);
      setSelectedMinute(minute);
      setSelectedPeriod(period);
    }
  }, [value]);

  const handleChange = (type: 'hour' | 'minute' | 'period', val: string) => {
    let hour = selectedHour;
    let minute = selectedMinute;
    let period = selectedPeriod;

    if (type === 'hour') {
      hour = val;
      setSelectedHour(val);
      if (selectedMinute === '--') {
        // Reset minute to '00' if hour changes and minute is not set
        setSelectedMinute('00');
        minute = '00';
      }

      if (selectedPeriod === '--') {
        // Reset period to 'AM' if hour changes and period is not set
        setSelectedPeriod('AM');
        period = 'AM';
      }
    }

    if (type === 'minute') {
      minute = val;
      setSelectedMinute(val);
      if (selectedHour === '--') {
        // Reset hour to '01' if minute changes and hour is not set
        setSelectedHour('01');
        hour = '01';
      }
      if (selectedPeriod === '--') {
        // Reset period to 'AM' if minute changes and period is not set
        setSelectedPeriod('AM');
        period = 'AM';
      }
    }

    if (type === 'period') {
      period = val;
      setSelectedPeriod(val);
      if (selectedHour === '--') {
        // Reset hour to '01' if period changes and hour is not set
        setSelectedHour('01');
        hour = '01';
      }
      if (selectedMinute === '--') {
        // Reset minute to '00' if period changes and minute is not set
        setSelectedMinute('00');
        minute = '00';
      }
    }

    onChange(`${hour}:${minute} ${period}`);
  };

  return (
    <div className="max-w-xs ltr:text-left rtl:text-right">
      <label
        htmlFor={effectiveID}
        className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
      >
        {label} <span className="text-red-500">*</span>
      </label>
      <Popover className="relative">
        <Popover.Button
          id={effectiveID}
          className="flex w-1/3 items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          {formatLocalizedTime(
            `${selectedHour}:${selectedMinute} ${selectedPeriod}`
          )}
          <Clock className="ms-2 h-4 w-4 text-gray-500 dark:text-gray-400" />
        </Popover.Button>

        <Popover.Panel className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-800">
          <div className="flex gap-2 rtl:flex-row-reverse">
            <select
              className="w-1/3 rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              onChange={(e) => handleChange('hour', e.target.value)}
              value={selectedHour}
            >
              <option value="--" disabled>
                {t('time.HH')}
              </option>
              {hours.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
            <select
              className="w-1/3 rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              onChange={(e) => handleChange('minute', e.target.value)}
              value={selectedMinute}
            >
              <option value="--" disabled>
                {t('time.MM')}
              </option>
              {minutes.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              className="w-1/3 rounded border border-gray-300 p-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              onChange={(e) => handleChange('period', e.target.value)}
              value={selectedPeriod}
            >
              <option value="--" disabled>
                {t('time.AM/PM')}
              </option>
              {periods.map((p) => (
                <option key={p} value={p}>
                  {t(`time.${p}`)}
                </option>
              ))}
            </select>
          </div>
        </Popover.Panel>
      </Popover>
    </div>
  );
};
